import React from "react";

export const AGENT_CLIENT_EXAMPLES = {
  mcp: {
    mcpServers: {
      grupa: {
        command: "grupa",
        args: ["mcp"],
        env: { GRUPAAI_BASE_URL: "https://api.staging.grupa.ai" },
      },
    },
  },
};

export default function AgentClientsGuide() {
  return (
    <>
      <section id="agent-clients" className="docs-section">
        <h2 className="docs-section-title">Claude Code, CLI &amp; MCP</h2>
        <p className="docs-text">
          Use the Grupa CLI or stdio MCP adapter to let an agent client start work,
          read progress, send feedback, answer questions, continue assignments, and
          retrieve files through the same Worker API. The adapter manages cloud runs;
          connecting a local browser requires the separate bridge described below.
        </p>
        <h3 className="docs-subtitle">Install and connect</h3>
        <ol className="docs-list">
          <li>With authorized source access and Node.js 22+, follow the <a href="https://github.com/grupaai/grupaai-demo/tree/main/sdk/agent-tools#readme" target="_blank" rel="noopener noreferrer">CLI and MCP setup guide</a> to build and install the local package. The CLI is source-distributed, not a public npm release. <a href="/api">Request pilot access</a> if you do not have source access.</li>
          <li>Inject <code>GRUPAAI_API_KEY</code> through your MCP host&apos;s secret environment. Set <code>GRUPAAI_BASE_URL</code> to the API supplied with your access. Keep keys out of prompts, task JSON, and source files.</li>
          <li>Have Claude Code or another MCP host launch <code>grupa mcp</code>. Use <code>grupa capabilities</code> to inspect exact operation schemas before sending requests.</li>
        </ol>
        <div className="docs-code-block">
          <div className="docs-code-header"><span className="docs-lang">MCP HOST CONFIGURATION</span></div>
          <pre className="docs-code-content" style={{ margin: 0 }}>{JSON.stringify(AGENT_CLIENT_EXAMPLES.mcp, null, 2)}</pre>
        </div>
        <p className="docs-text">
          The configuration above assumes <code>grupa</code> is on the host&apos;s PATH and
          the host supplies <code>GRUPAAI_API_KEY</code> securely. The transport is stdio;
          there is no public HTTP MCP endpoint advertised by this reference.
        </p>
        <p className="docs-text">
          Available tools include <code>grupa_run_start</code>, <code>grupa_run_trace</code>,{" "}
          <code>grupa_run_feedback</code>, <code>grupa_run_continue</code>, client-tool
          claim/output operations, and <code>grupa_tool_artifact_upload</code>. Read the
          MCP resource <code>grupa://docs/usage</code> for lifecycle and recovery guidance.
          API tenant boundaries, scopes, approvals, deadlines, quotas, and budgets still apply.
        </p>
      </section>

      <section id="local-browser" className="docs-section">
        <h2 className="docs-section-title">Local Browser Control</h2>
        <div className="docs-callout">
          <div className="docs-callout-title">Connector required</div>
          <p className="docs-callout-text">The tested Windows pilot uses an owned test profile. Customer pairing,
            personal browser sessions, and macOS/Linux are not yet validated. This
            connects an explicitly authorized browser, not an entire device.</p>
        </div>
        <p className="docs-text">
          Managed cloud desktops remain the default. A local bridge can connect an
          authorized browser executor, such as Playwright MCP, so a cloud worker can
          select actions for that browser. Sign in locally. Passwords, cookies, and
          browser profiles are not uploaded as run resources; task page content and
          retained screenshots or video can reach the cloud.
        </p>
        <h3 className="docs-subtitle">Connect before starting work</h3>
        <ol className="docs-list">
          <li>Set up the bridge using the <a href="https://github.com/grupaai/grupaai-demo/tree/main/examples/local-browser#readme" target="_blank" rel="noopener noreferrer">local browser pilot guide</a>. Register the executor&apos;s actual tool names and schemas in <code>resources.customTools</code>, with <code>execution: &quot;client&quot;</code>.</li>
          <li>Use <code>worker.mode: &quot;single&quot;</code> without persistence and <code>output.captureSource: &quot;client&quot;</code>. Start the run only when the bridge is ready to service its tools. Native managed-desktop input is withheld in client capture mode.</li>
          <li>Read <code>GET /v1/runs/{"{runId}"}/tool-calls</code>, exclusively claim the call, execute it once on the selected browser, persist its result, then submit output. See <a href="#client-tools">Application Tools &amp; Deliverables</a> for the claim/output protocol.</li>
        </ol>
        <div className="docs-callout">
          <div className="docs-callout-title">Extraction-only jobs</div>
          <p className="docs-callout-text">Jobs classified as extraction-only currently block remote tools,
            including connected-browser tools, to enforce the read-only boundary.
            Not every read-only research task can use this connection.</p>
        </div>
        <p className="docs-text">
          An uncertain output delivery must not repeat a click or submission. Retain
          claim ownership and the result for identical retries. Waiting for a client
          tool still consumes the active run&apos;s deadline and execution charges.
        </p>
        <h3 className="docs-subtitle">Retain browser evidence</h3>
        <p className="docs-text">
          Upload source bytes to <code>PUT /v1/runs/{"{runId}"}/tool-calls/{"{toolCallId}"}/artifacts/{"{artifactId}"}</code>.
          Supply Bearer authentication, your exclusive <code>X-GrupaAI-Claim-ID</code>,
          and <code>X-GrupaAI-Artifact-Kind</code> as <code>screenshot</code> or <code>recording</code>.
          The SDK&apos;s <code>context.uploadArtifact</code> calculates the artifact ID.
          CLI/MCP uploads require an explicit <code>GRUPAAI_EVIDENCE_DIR</code>.
        </p>
        <ul className="docs-list">
          <li><strong>Screenshots:</strong> PNG or JPEG, at most 8 MiB and 16 million pixels. When requested, screenshot masking happens on the control plane before storage. The original image reaches the control plane.</li>
          <li><strong>Recordings:</strong> WebM or MP4, at most 64 MiB. Enable <code>output.recording: true</code> before execution with recording consent. Video remains unmasked, including when screenshot masking is enabled.</li>
          <li><strong>Finalization:</strong> a completed claim can finalize its recording within five minutes of termination.</li>
          <li><strong>Immutable receipts:</strong> IDs bind the tool call and source-content hash. A new artifact returns <code>201</code>; an identical retry returns <code>200</code> without overwriting evidence. Arbitrary URLs are never fetched.</li>
        </ul>
        <p className="docs-text">
          Read <code>/live-preview</code> for the authenticated browser preview and{" "}
          <code>/artifacts</code> for retained proof and download URLs. The run&apos;s{" "}
          <code>captureSource</code> identifies <code>cloud</code> or <code>client</code> evidence.
        </p>
        <p className="docs-text">
          A Playground connection flow is in development through the{" "}
          <a href="https://console.staging.grupa.ai/templates?template=local-browser" target="_blank" rel="noopener noreferrer">Local browser control template</a>.
          {" "}It requires a separately started, authorized connector on your computer.
          Keep the Playground open while it relays the run. This flow, personal browser
          sessions, and cross-platform operation still require independent end-to-end
          verification; an API request alone cannot grant laptop access.
        </p>
      </section>
    </>
  );
}
